// stores objects: { listeners: [], cachedSelector: "" }
const registry = {};

const patchEventPropagation = (event, state) => {
  const originalStop = event.stopPropagation;
  event.stopPropagation = () => {
    state.propagationStopped = true;
    if (event.eventPhase !== Event.CAPTURING_PHASE) {
      originalStop.call(event);
    }
  };

  const originalStopImmediate = event.stopImmediatePropagation;
  event.stopImmediatePropagation = () => {
    state.propagationStopped = true;
    state.immediatePropagationStopped = true;
    if (event.eventPhase !== Event.CAPTURING_PHASE) {
      originalStopImmediate.call(event);
    }
  };
};

const overrideCurrentTarget = (event, node, descriptor) => {
  if (!descriptor) return;

  Object.defineProperty(event, "currentTarget", {
    configurable: true,
    enumerable: true,
    get: () => node
  });
};

const restoreCurrentTarget = (event, descriptor) => {
  if (descriptor) {
    Object.defineProperty(event, "currentTarget", descriptor);
  } else {
    delete event.currentTarget;
  }
};

const getMatchedListeners = (node, listeners) => {
  if (!node.matches) return [];

  const matches = [];
  // micro-optimization: standard for-loop instead of .filter()
  for (let i = 0; i < listeners.length; i++) {
    if (node.matches(listeners[i].selector)) {
      matches.push(listeners[i]);
    }
  }
  return matches;
};

const executeMatchedHandlers = (node, event, matchedListeners, state) => {
  for (let i = 0; i < matchedListeners.length; i++) {
    if (state.immediatePropagationStopped) break;

    matchedListeners[i].handler.call(node, event);
  }
};

const getRegistryKey = (eventName, isCapture) => `${eventName}-${isCapture ? "capture" : "bubble"}`;

const globalDispatcher = (event) => {
  const isCapture = event.eventPhase === Event.CAPTURING_PHASE;
  const registryKey = getRegistryKey(event.type, isCapture);

  const registryEntry = registry[registryKey];
  if (!registryEntry || registryEntry.listeners.length === 0) return;

  const { listeners, cachedSelector } = registryEntry;

  try {
    if (!event.target.closest || !event.target.closest(cachedSelector)) {
      return; // exit instantly! No JS loops required
    }
  } catch {
    // failsafe for invalid selectors
  }

  const state = {
    propagationStopped: false,
    immediatePropagationStopped: false
  };
  patchEventPropagation(event, state);

  const currentTargetDesc = Object.getOwnPropertyDescriptor(Event.prototype, "currentTarget");
  let currentNode = event.target;

  // walk up the DOM tree
  while (currentNode && currentNode !== document) {
    if (state.propagationStopped) break;

    const matchedListeners = getMatchedListeners(currentNode, listeners);

    if (matchedListeners.length > 0) {
      overrideCurrentTarget(event, currentNode, currentTargetDesc);
      executeMatchedHandlers(currentNode, event, matchedListeners, state);
    }
    // move to parent node
    currentNode = currentNode.parentNode;
  }

  restoreCurrentTarget(event, currentTargetDesc);
};

export const on = (eventName, selector, handler, options = {}) => {
  const useCapture = options.capture === true;
  const registryKey = getRegistryKey(eventName, useCapture);

  if (!registry[registryKey]) {
    registry[registryKey] = { listeners: [], cachedSelector: "" };
    document.addEventListener(eventName, globalDispatcher, useCapture);
  }

  registry[registryKey].listeners.push({ selector, handler });

  // cache the selector string immediately when a new listener is added
  registry[registryKey].cachedSelector = registry[registryKey].listeners
    .map((l) => l.selector)
    .join(",");
};

export const off = (eventName, selector, handler, options = {}) => {
  const useCapture = options.capture === true;
  const registryKey = getRegistryKey(eventName, useCapture);

  if (!registry[registryKey]) return;

  registry[registryKey].listeners = registry[registryKey].listeners.filter(
    (listener) => !(listener.selector === selector && listener.handler === handler)
  );

  if (registry[registryKey].listeners.length === 0) {
    delete registry[registryKey];
    document.removeEventListener(eventName, globalDispatcher, useCapture);
  } else {
    // re-cache the selector string without the removed listener
    registry[registryKey].cachedSelector = registry[registryKey].listeners
      .map((l) => l.selector)
      .join(",");
  }
};

export const fire = (target, name, detail) => {
  return target.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      detail: detail
    })
  );
};
